import React, { useEffect, useState } from 'react';
import { Tabs, Typography, Spin, Avatar, Tag, Button } from 'antd';
import {
  BarChartOutlined, CalendarOutlined, LineChartOutlined,
  UserOutlined, ClockCircleOutlined, TeamOutlined, MailOutlined,
} from '@ant-design/icons';
import api, { PLAYER_ACTIVE_TEAM_KEY } from '../../services/api';
import StatsTab from './player/StatsTab';
import GamesTab from './player/GamesTab';
import FrequencyTab from './player/FrequencyTab';
import ProfileCompletionCard from '../../components/community/ProfileCompletionCard';
import ConvitesPanel from '../../components/community/ConvitesPanel';
import NearbyGamesCard from '../../components/community/NearbyGamesCard';
import CommunityFeed from '../../components/community/CommunityFeed';
import CommunityHighlights from '../../components/community/CommunityHighlights';
import SlugChooserCard from '../../components/community/SlugChooserCard';
import PushOnboardingCard from '../../components/community/PushOnboardingCard';
import PlayerXpCard from '../../components/community/PlayerXpCard';
import MyTeamsNewsCard from '../../components/community/MyTeamsNewsCard';
import OnboardingModal from '../../components/community/OnboardingModal';

const { Title, Text } = Typography;
const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const GRID_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><path d='M60 0H0v60' fill='none' stroke='rgba(255,255,255,0.08)' stroke-width='1'/></svg>\")";

// SVG bola de futebol (decorativa)
const BallSvg = ({ size = 240, opacity = 0.12 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" style={{ opacity }} xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="45" fill="#fff" />
    <polygon points="50,25 60,32 56,44 44,44 40,32" fill="#000" />
    <polygon points="30,45 40,32 44,44 38,55 26,55" fill="#000" />
    <polygon points="70,45 60,32 56,44 62,55 74,55" fill="#000" />
    <polygon points="50,75 62,68 62,55 38,55 38,68" fill="#000" />
    <circle cx="50" cy="50" r="45" fill="none" stroke="#000" strokeWidth="1" />
  </svg>
);

function parseBrDateTime(str) {
  // Ex: "20/07/2026 21:00" ou "20/07/2026"
  if (!str) return null;
  const m = String(str).match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
  if (!m) return null;
  const [, dd, mm, yyyy, hh = '00', mi = '00'] = m;
  return new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:00-03:00`);
}

const PlayerDashboard = () => {
  // Auth já foi validada pelo CommunityLayout (sincrono, antes desse render).
  // Aqui só lê o user do localStorage — inicializador sincrono evita flash.
  const [player] = useState(() => {
    const raw = localStorage.getItem('community_user');
    return raw ? JSON.parse(raw) : null;
  });
  const [activeCustomerId, setActiveCustomerId] = useState(() => {
    const v = localStorage.getItem(PLAYER_ACTIVE_TEAM_KEY);
    return v ? parseInt(v, 10) : null;
  });
  const [nextGame, setNextGame] = useState(null);

  useEffect(() => {
    const onTeamChange = (e) => setActiveCustomerId(e.detail?.customer_id || null);
    window.addEventListener('active_customer_changed', onTeamChange);
    return () => window.removeEventListener('active_customer_changed', onTeamChange);
  }, []);

  // Busca próximo jogo (futuro mais próximo)
  useEffect(() => {
    if (!activeCustomerId) return;
    (async () => {
      try {
        const r = await api.get('/community/player/games');
        const now = Date.now();
        const upcoming = (r.data?.games || [])
          .map(g => ({ ...g, _ts: parseBrDateTime(g.date_of_game)?.getTime() || 0 }))
          .filter(g => g._ts > now)
          .sort((a, b) => a._ts - b._ts);
        setNextGame(upcoming[0] || null);
      } catch { /* silencioso */ }
    })();
  }, [activeCustomerId]);

  if (!player) return null;

  if (!activeCustomerId) {
    // Jogador sem time ATIVO: pode continuar usando a Comunidade
    // (procurar peladas, mapa, perfil). Feed/highlights/times-proximos
    // aparecem abaixo; tabs de time (stats/jogos/frequencia) precisam
    // de time e ficam ocultas.
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16 }}>
        <OnboardingModal />
        <PlayerXpCard />
        <MyTeamsNewsCard />
        <PushOnboardingCard />
        <SlugChooserCard />
        <ProfileCompletionCard />
        <div style={{
          background: '#fff', borderRadius: 16, padding: 24, marginBottom: 20,
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⚽</div>
          <Title level={4} style={{ marginTop: 0 }}>Você ainda não tá em nenhum time</Title>
          <Text type="secondary">
            Sem problema — dá pra procurar peladas perto de você, ver o mapa e criar seu perfil.
            Quando um organizador te adicionar num time, aparece aqui.
          </Text>
          <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button type="primary" size="large" style={{ background: BRAND, borderColor: BRAND }}
              onClick={() => window.location.href = '/player/procurar'}>Procurar peladas</Button>
            <Button size="large" onClick={() => window.location.href = '/player/map'}>Ver mapa</Button>
          </div>
        </div>
        <NearbyGamesCard />
        <CommunityHighlights />
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <CommunityFeed compact />
        </div>
      </div>
    );
  }

  const firstName = player.name?.split(' ')[0] || 'jogador';
  const nowH = new Date().getHours();
  const greeting = nowH < 12 ? 'Bom dia' : nowH < 18 ? 'Boa tarde' : 'Boa noite';

  const key = `k-${activeCustomerId}`;
  const items = [
    { key: 'stats',     label: <><BarChartOutlined /> Estatísticas</>, children: <StatsTab key={`s-${key}`} /> },
    { key: 'games',     label: <><CalendarOutlined /> Jogos</>,          children: <GamesTab key={`g-${key}`} /> },
    { key: 'frequency', label: <><LineChartOutlined /> Minha frequência</>, children: <FrequencyTab key={`f-${key}`} /> },
    { key: 'invites',   label: <><MailOutlined /> Convites</>,          children: <ConvitesPanel userType="player" /> },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16 }}>
      <OnboardingModal />
      <PlayerXpCard />
      <MyTeamsNewsCard />
      <PushOnboardingCard />
      <SlugChooserCard />
      <ProfileCompletionCard />

      {/* Hero */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
        borderRadius: 20, padding: 24, color: '#fff',
        boxShadow: '0 12px 32px rgba(37,211,102,0.25)', marginBottom: 20,
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: GRID_BG, opacity: 0.5 }} />
        <div style={{ position: 'absolute', right: -60, top: -40 }}>
          <BallSvg size={260} opacity={0.10} />
        </div>
        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)',
          top: -70, left: -70 }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar
            size={64}
            src={player.profile_photo}
            icon={<UserOutlined />}
            style={{
              background: player.profile_photo ? 'transparent' : 'rgba(255,255,255,0.2)',
              border: '3px solid rgba(255,255,255,0.4)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            }}
          />
          <div style={{ flex: 1 }}>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>{greeting},</Text>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.1 }}>{firstName} 👋</div>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
              Sua central: estatísticas, jogos e frequência.
            </Text>
          </div>
        </div>

        {/* Próximo jogo em destaque */}
        {nextGame && (
          <div style={{
            position: 'relative', marginTop: 20, padding: 16,
            background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.18)', borderRadius: 14,
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'rgba(255,255,255,0.16)', display: 'grid', placeItems: 'center',
              fontSize: 22, flexShrink: 0,
            }}>📅</div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', letterSpacing: 1 }}>PRÓXIMO JOGO</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{nextGame.date_of_game}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{nextGame.team_name}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {nextGame.went && <Tag color="green" style={{ margin: 0 }}>Confirmado</Tag>}
              {nextGame.waitlist && <Tag color="orange" style={{ margin: 0 }}>Lista de espera</Tag>}
              {!nextGame.went && !nextGame.waitlist && !nextGame.response_at && (
                <Tag color="default" style={{ margin: 0, background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}>
                  Sem resposta
                </Tag>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Feed de jogos proximos */}
      <NearbyGamesCard />

      {/* Highlights da semana */}
      <CommunityHighlights />

      {/* Feed da comunidade (novos jogadores + times) */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.04)', marginBottom: 20 }}>
        <CommunityFeed compact />
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '4px 20px 20px 20px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        <Tabs
          defaultActiveKey="stats"
          items={items}
          destroyInactiveTabPane
          size="large"
          tabBarStyle={{ marginBottom: 20 }}
        />
      </div>
    </div>
  );
};

export default PlayerDashboard;
